import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Slide, Box, Typography, IconButton } from '@mui/material';
import { X, CheckCircle, AlertTriangle, Info, XCircle } from 'lucide-react';

const NotificationContext = createContext();

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);

  const showNotification = useCallback((message, severity = 'success', duration = 4000) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, severity, duration }]);
  }, []);

  const hideNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const success = useCallback((message) => showNotification(message, 'success'), [showNotification]);
  const error = useCallback((message) => showNotification(message, 'error'), [showNotification]);
  const warning = useCallback((message) => showNotification(message, 'warning'), [showNotification]);
  const info = useCallback((message) => showNotification(message, 'info'), [showNotification]);

  const getIcon = (severity) => {
    switch (severity) {
      case 'success': return <CheckCircle size={20} />;
      case 'error': return <XCircle size={20} />;
      case 'warning': return <AlertTriangle size={20} />;
      case 'info': return <Info size={20} />;
      default: return <Info size={20} />;
    }
  };

  return (
    <NotificationContext.Provider value={{ showNotification, success, error, warning, info }}>
      {children}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={notification.duration}
          onClose={() => hideNotification(notification.id)}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            bottom: { xs: 24 + (index * 70), sm: 24 + (index * 70) },
            '& .MuiSnackbarContent-root': {
              minWidth: 'auto',
            },
          }}
        >
          <Alert
            onClose={() => hideNotification(notification.id)}
            severity={notification.severity}
            variant="filled"
            icon={getIcon(notification.severity)}
            sx={{
              minWidth: 300,
              borderRadius: 3,
              boxShadow: '0 10px 40px -10px rgb(0 0 0 / 0.3)',
              alignItems: 'center',
              '& .MuiAlert-icon': {
                fontSize: 24,
              },
              '& .MuiAlert-message': {
                fontWeight: 500,
              },
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
